import { fetchCar3dModels, type Car3dModelItem } from '../cars/api.js';
import { mountLazyCar3dPreview } from '../cars/car3dPreviewGrid.js';
import {
  ASSET_PICKER_BATCH_SIZE,
  attachAssetPickerScrollSentinel,
  type AssetPickerScrollSentinelHandle,
} from './assetPickerScroll.js';

export type Car3dModelPickerOptions = {
  title: string;
  /** Modèle actuellement assigné (publicPath), s'il y en a un. */
  currentPath?: string | null;
  /** `null` = réinitialiser vers le choix automatique par mot-clé sur le nom. */
  onSelect: (item: Car3dModelItem | null) => void;
  /** Si fourni, affiche un bouton « Depuis mon PC » pour uploader un .glb personnel. */
  onUploadFromPc?: (file: File) => Promise<void>;
  onError?: (message: string) => void;
};

function closeDialog(
  dialog: HTMLDialogElement,
  cleanups: Array<() => void>,
  sentinelHandle: AssetPickerScrollSentinelHandle | null
): void {
  sentinelHandle?.disconnect();
  cleanups.forEach((fn) => fn());
  cleanups.length = 0;
  dialog.close();
  dialog.remove();
}

/** Ouvre une modale pour choisir un modèle 3D (ou revenir à l'automatique) parmi le catalogue. */
export function openCar3dModelPicker(options: Car3dModelPickerOptions): void {
  const dialog = document.createElement('dialog');
  dialog.className = 'asset-picker-dialog';
  dialog.setAttribute('aria-labelledby', 'car3d-picker-title');

  const inner = document.createElement('div');
  inner.className = 'asset-picker-dialog-inner';

  const head = document.createElement('div');
  head.className = 'asset-picker-head';
  head.innerHTML = `<h2 id="car3d-picker-title" class="asset-picker-title">${options.title}</h2>`;

  const cleanups: Array<() => void> = [];
  let sentinelHandle: AssetPickerScrollSentinelHandle | null = null;

  const close = (): void => closeDialog(dialog, cleanups, sentinelHandle);

  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'btn btn-ghost asset-picker-close';
  closeBtn.textContent = 'Fermer';
  closeBtn.addEventListener('click', close);
  head.appendChild(closeBtn);

  if (options.onUploadFromPc) {
    const uploadLabel = document.createElement('label');
    uploadLabel.className = 'entity-image-file-label';
    uploadLabel.innerHTML =
      '<span class="btn btn-secondary btn-sm">Depuis mon PC (.glb)</span>' +
      '<input type="file" accept=".glb" class="car3d-picker-upload-input" hidden />';
    const uploadInput = uploadLabel.querySelector('input') as HTMLInputElement;
    uploadInput.addEventListener('change', () => {
      const file = uploadInput.files?.[0];
      if (!file) return;
      uploadInput.disabled = true;
      status.hidden = false;
      status.textContent = 'Envoi en cours…';
      void options.onUploadFromPc!(file)
        .then(() => close())
        .catch((err: Error) => {
          const message = err.message || "Échec de l'envoi du modèle 3D.";
          status.textContent = message;
          options.onError?.(message);
        })
        .finally(() => {
          uploadInput.disabled = false;
          uploadInput.value = '';
        });
    });
    head.appendChild(uploadLabel);
  }

  const search = document.createElement('input');
  search.type = 'search';
  search.className = 'asset-picker-search';
  search.placeholder = 'Rechercher un modèle…';
  search.setAttribute('aria-label', 'Filtrer les modèles 3D');

  const grid = document.createElement('div');
  grid.className = 'asset-picker-grid car3d-picker-grid';
  grid.setAttribute('role', 'listbox');
  grid.setAttribute('aria-label', 'Modèles 3D disponibles');

  const status = document.createElement('p');
  status.className = 'asset-picker-status';
  status.textContent = 'Chargement…';

  inner.append(head, search, status, grid);
  dialog.appendChild(inner);
  document.body.appendChild(dialog);

  dialog.addEventListener('click', (e) => {
    if (e.target === dialog) close();
  });
  dialog.addEventListener('cancel', () => close());

  let items: Car3dModelItem[] = [];
  let filtered: Car3dModelItem[] = [];
  let visibleCount = ASSET_PICKER_BATCH_SIZE;

  function createItemButton(item: Car3dModelItem): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'car3d-picker-item';
    if (item.publicPath === options.currentPath) btn.classList.add('is-selected');
    btn.setAttribute('role', 'option');
    btn.title = item.slug;

    const previewWrap = document.createElement('div');
    previewWrap.className = 'car3d-picker-item-preview';

    const cap = document.createElement('span');
    cap.className = 'asset-picker-item-name';
    cap.textContent = item.slug;

    btn.append(previewWrap, cap);
    btn.addEventListener('click', () => {
      options.onSelect(item);
      close();
    });
    // `root: grid` : la grille défile dans sa propre zone (max-height + overflow-y).
    cleanups.push(mountLazyCar3dPreview(previewWrap, item.publicPath, grid));
    return btn;
  }

  function attachSentinel(): void {
    sentinelHandle?.disconnect();
    sentinelHandle = attachAssetPickerScrollSentinel(
      grid,
      filtered.length > visibleCount,
      appendNextBatch
    );
  }

  /**
   * Ajoute le lot suivant sans remonter les aperçus 3D déjà chargés
   * (voir circuitPiecePicker.ts — un renderGrid complet orphelinait la file WebGL).
   */
  function appendNextBatch(): void {
    const next = filtered.slice(visibleCount, visibleCount + ASSET_PICKER_BATCH_SIZE);
    visibleCount += ASSET_PICKER_BATCH_SIZE;
    sentinelHandle?.disconnect();
    sentinelHandle = null;
    next.forEach((item) => grid.appendChild(createItemButton(item)));
    attachSentinel();
  }

  const renderGrid = (): void => {
    cleanups.forEach((fn) => fn());
    cleanups.length = 0;
    sentinelHandle?.disconnect();
    sentinelHandle = null;
    grid.innerHTML = '';

    const autoBtn = document.createElement('button');
    autoBtn.type = 'button';
    autoBtn.className = 'car3d-picker-item car3d-picker-item--auto';
    if (!options.currentPath) autoBtn.classList.add('is-selected');
    autoBtn.setAttribute('role', 'option');
    autoBtn.title = 'Choisi automatiquement selon le nom de la voiture';
    autoBtn.innerHTML =
      '<span class="car3d-picker-auto-icon" aria-hidden="true">✨</span>' +
      '<span class="asset-picker-item-name">Automatique</span>';
    autoBtn.addEventListener('click', () => {
      options.onSelect(null);
      close();
    });
    grid.appendChild(autoBtn);

    const q = search.value.trim().toLowerCase();
    filtered = q ? items.filter((it) => it.slug.toLowerCase().includes(q)) : items;

    if (filtered.length === 0 && q) {
      status.hidden = false;
      status.textContent = 'Aucun modèle ne correspond.';
    } else {
      status.hidden = true;
    }

    filtered.slice(0, visibleCount).forEach((item) => grid.appendChild(createItemButton(item)));
    attachSentinel();
  };

  search.addEventListener('input', () => {
    visibleCount = ASSET_PICKER_BATCH_SIZE;
    renderGrid();
  });

  dialog.showModal();

  void fetchCar3dModels()
    .then((models) => {
      items = models;
      status.textContent = `${items.length} modèle(s)`;
      renderGrid();
    })
    .catch((err: Error) => {
      status.hidden = false;
      status.textContent = err.message || 'Impossible de charger les modèles 3D.';
      options.onError?.(status.textContent);
    });
}
